import type { SeattleGameState, SelectActionSeattleArgs, SeattleActionType } from '../../types';
import type { Ctx } from 'boardgame.io';

export interface ValidationResult {
  success: boolean;
  error?: string;
  message?: string;
}

/**
 * アクション選択のバリデーション（Seattle固有）
 *
 * @param G - Seattle ゲーム状態
 * @param _ctx - Boardgame.io コンテキスト
 * @param args - アクション選択引数
 * @returns バリデーション結果
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_03_select_actions_seattle.md
 */
export function validateSelectActionSeattle(
  G: SeattleGameState,
  _ctx: Ctx,
  args: SelectActionSeattleArgs
): ValidationResult {
  const player = G.players['0'];

  // Seattle専用アクションチェック（2種類のみ）
  const validActions: SeattleActionType[] = ['ENGINEER', 'LOCOMOTIVE'];

  if (!validActions.includes(args.action)) {
    return {
      success: false,
      error: 'INVALID_ACTION_TYPE',
      message: `Action ${args.action} is not available in Seattle solo play. Valid actions: ${validActions.join(', ')}`
    };
  }

  // 重複アクションチェック（同一ターン内で同じアクションは選択不可）
  if (player.actionsSelectedThisTurn.includes(args.action)) {
    return {
      success: false,
      error: 'DUPLICATE_ACTION',
      message: `Action ${args.action} has already been selected this turn`
    };
  }

  // Locomotive 選択時の locomotiveEffect 必須チェック
  if (args.action === 'LOCOMOTIVE') {
    if (!args.locomotiveEffect) {
      return {
        success: false,
        error: 'MISSING_LOCOMOTIVE_EFFECT',
        message: 'locomotiveEffect is required when selecting LOCOMOTIVE action'
      };
    }

    // LOCO_UP の場合、機関車レベル上限チェック
    if (args.locomotiveEffect === 'LOCO_UP' && player.locomotiveLevel >= 6) {
      return {
        success: false,
        error: 'LOCO_ALREADY_MAX',
        message: 'Locomotive level is already at maximum (6)'
      };
    }

    // CARGO_UP の場合、貨物輸送レベル上限チェック
    if (args.locomotiveEffect === 'CARGO_UP' && G.cargoTransportLevel >= 5) {
      return {
        success: false,
        error: 'CARGO_ALREADY_MAX',
        message: 'Cargo transport level is already at maximum (5)'
      };
    }
  }

  return { success: true };
}

/**
 * 選択可能なアクションのリストを取得
 * @param G - Seattle ゲーム状態
 * @returns 選択可能なアクションの配列
 */
export function getAvailableActionsSeattle(G: SeattleGameState): SeattleActionType[] {
  const player = G.players['0'];
  const allActions: SeattleActionType[] = ['ENGINEER', 'LOCOMOTIVE'];

  // 今ターンに既に選択したアクションを除外
  return allActions.filter(action => !player.actionsSelectedThisTurn.includes(action));
}

/**
 * Locomotive アクションで選択可能な効果を取得
 * @param G - Seattle ゲーム状態
 * @returns 選択可能な効果の配列
 */
export function getAvailableLocomotiveEffects(G: SeattleGameState): ('LOCO_UP' | 'CARGO_UP')[] {
  const player = G.players['0'];
  const effects: ('LOCO_UP' | 'CARGO_UP')[] = [];

  if (player.locomotiveLevel < 6) {
    effects.push('LOCO_UP');
  }
  if (G.cargoTransportLevel < 5) {
    effects.push('CARGO_UP');
  }

  return effects;
}
